import React, { useState, useRef } from 'react';
import { Image as ImageIcon, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface ChatImageUploadProps {
  onImageSelect: (file: File, previewUrl: string) => void;
  onImageRemove: () => void;
  selectedImage: { file: File; previewUrl: string } | null;
  disabled?: boolean;
}

export const ChatImageUpload: React.FC<ChatImageUploadProps> = ({
  onImageSelect,
  onImageRemove,
  selectedImage,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }

      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        toast.error('Image size must be less than 10MB');
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      onImageSelect(file, previewUrl);
    }

    // Reset input to allow selecting the same file again
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        disabled={disabled}
        className="hidden"
      />
      
      {selectedImage ? (
        <div className="relative inline-block">
          <img
            src={selectedImage.previewUrl}
            alt="Selected"
            className="h-20 w-20 rounded-lg object-cover border border-brand-green-border"
          />
          <button
            type="button"
            onClick={onImageRemove}
            className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-destructive text-destructive-foreground flex items-center justify-center hover:bg-destructive/90 transition-colors"
            aria-label="Remove image"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={handleButtonClick}
          disabled={disabled}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Add image"
          title="Upload an image"
        >
          <ImageIcon className="h-4 w-4 text-muted-foreground" />
        </button>
      )}
    </>
  );
};
